import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Box, Button, Typography, Pill} from 'views/components';
import {Autorenew as RegenerateIcon, Edit as EditIcon} from 'views/icons';
import {translate, SimpleRefreshWidget} from 'views/module';

import JobStatus from './job_status/JobStatus';
import PriorityDropdown from './dropdowns/Priority';

import moment from 'moment';

const JobDetails = (props) => {
  const {
    jobData,
    client,
    jobBackend,
    refresh,
    setRefresh,
    showJobTypePill,
    loomJob,
    onEditJob,
    onRegenerateJob,
  } = props;

  const [lastUpdatedAt, setLastUpdatedAt] = React.useState(new Date());
  const [priority, setPriority] = React.useState(jobData?.priority || null);
  const [priorityLoading, setPriorityLoading] = React.useState(false);

  const nullChar = '-';

  React.useEffect(() => {
    setPriority(jobData?.priority || null);
  }, [jobData?.priority]);

  const jobCreatedAt = jobData.createdAt ?? null;
  const daysFromCreation = React.useMemo(() => {
    if (!jobCreatedAt) return 0;
    return moment().diff(moment(jobCreatedAt), 'days');
  }, [jobCreatedAt]);
  const jobLocation = [jobData?.location?.neighborhood, jobData?.location?.city].filter(Boolean).join(', ') || nullChar;

  const expectedStartDate = React.useMemo(() => {
    if (!jobCreatedAt) {
      return nullChar;
    }

    const createdAtMoment = moment(jobCreatedAt);
    const startTime = jobData?.startDate?.startTime;

    if (startTime === 'IMMEDIATELY') {
      return createdAtMoment.format('MM/DD/YYYY');
    }

    if (startTime === 'ONEWEEK') {
      return createdAtMoment.clone().add(1, 'week').format('MM/DD/YYYY');
    }

    if (startTime === 'TWOWEEK') {
      return createdAtMoment.clone().add(2, 'weeks').format('MM/DD/YYYY');
    }

    if (startTime === 'ONEMONTH') {
      return createdAtMoment.clone().add(1, 'month').format('MM/DD/YYYY');
    }

    if (startTime === 'TWOMONTH') {
      return createdAtMoment.clone().add(2, 'months').format('MM/DD/YYYY');
    }

    if (startTime === 'FLEXIBLE') {
      return translate('career.jobDetail.Information.FlexibleStartTiming');
    }

    return nullChar;
  }, [jobCreatedAt, jobData]);

  const clientName = client?.clientDetail?.clientName || jobData?.client?.unregisteredClient || nullChar;

  // Exactly one of clientRn and unregisteredClient is ever set: the create flow writes one or
  // the other, and Loom's ingest rejects a job carrying both or neither.
  const jobType = jobData?.client?.clientRn ? 'CLIENT' : 'GENERAL';

  const handleSetPriority = (selectedPriority) => {
    if (selectedPriority === priority) {
      return;
    }

    const previousPriority = priority;

    setPriority(selectedPriority);
    setPriorityLoading(true);

    jobBackend.job.editPriority(
        selectedPriority,
        () => {
          setPriorityLoading(false);
        },
        () => {
          setPriority(previousPriority);
          setPriorityLoading(false);
        },
    );
  };

  const metricsSections = React.useMemo(() => {
    return [
      {
        key: 'clientName',
        label: translate('career.jobDetail.Metrics.ClientName'),
        value: clientName,
      },
      {
        key: 'priority',
        label: translate('career.jobDetail.Metrics.Priority'),
        type: 'priorityDropdown',
      },
      {
        key: 'startDate',
        label: translate('career.jobDetail.Metrics.StartDate'),
        value: expectedStartDate,
      },
    ].filter((section) => !loomJob || section.key !== 'priority');
  }, [clientName, expectedStartDate, loomJob]);

  return (
    <Stack sx={{gap: '10px'}}>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems={{xs: 'flex-start', md: 'center'}}
        sx={{gap: '10px', flexWrap: 'wrap'}}
      >
        <Stack
          direction='row'
          sx={{
            gap: '7px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <JobStatus
            jobData={jobData}
            jobBackend={jobBackend}
          />
          <Pill
            label={translate('client.ClientDetail.Status.Subtitles.Day', {number: daysFromCreation})}
            variant='info'
          />
          {
            showJobTypePill &&
            <Pill
              label={translate(`career.JobType.${jobType}.Title`)}
              variant='info'
            />
          }
          {
            jobLocation !== nullChar &&
            <Pill
              label={jobLocation}
              variant='neutral'
            />
          }
          {
            jobCreatedAt &&
            <Pill
              label={translate('client.ClientDetail.Status.Subtitles.Created', {
                value: moment(jobCreatedAt).format(translate('timeFormat.ShortMonthDayYear')),
              })}
              variant='neutral'
            />
          }
          <Pill
            label={translate('client.ClientDetail.Status.Subtitles.Updated', {
              value: moment(lastUpdatedAt).format(translate('timeFormat.ShortMonthDayTime')),
            })}
            variant='neutral'
          />
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          sx={{gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto'}}
        >
          <SimpleRefreshWidget
            numRefresh={refresh}
            setNumRefresh={setRefresh}
            setLastUpdatedAt={setLastUpdatedAt}
          />
          {
            loomJob &&
            <>
              <Button size='small' variant='outlined' startIcon={<EditIcon />} onClick={onEditJob}>
                {translate('job.ClientJobs.Edit')}
              </Button>
              <Button size='small' variant='outlined' startIcon={<RegenerateIcon />} onClick={onRegenerateJob}>
                {translate('job.ClientJobs.Regenerate')}
              </Button>
            </>
          }
        </Stack>
      </Stack>
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E8E4DE',
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gap: '1px',
            backgroundColor: '#E8E4DE',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, auto))',
              sm: `repeat(${metricsSections.length}, minmax(0, auto))`,
            },
          }}
        >
          {
            metricsSections.map((section) => {
              return (
                <Box
                  key={section.key}
                  sx={{
                    minWidth: 0,
                    backgroundColor: '#FFFFFF',
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {
                    section.type === 'priorityDropdown' &&
                    <PriorityDropdown
                      label={section.label}
                      loading={priorityLoading}
                      selectedPriority={priority}
                      setSelectedPriority={handleSetPriority}
                    />
                  }
                  {
                    !section.type &&
                    <Stack
                      sx={{
                        gap: '6px',
                        minWidth: 0,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: '11px',
                          lineHeight: '14px',
                          color: '#A8A49E',
                        }}
                      >
                        {section.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '12px',
                          lineHeight: '16px',
                          color: '#000000',
                          wordBreak: 'break-word',
                        }}
                      >
                        {section.value}
                      </Typography>
                    </Stack>
                  }
                </Box>
              );
            })
          }
        </Box>
      </Box>
    </Stack>
  );
};

JobDetails.propTypes = {
  jobData: PropTypes.object.isRequired,
  client: PropTypes.object,
  jobBackend: PropTypes.object.isRequired,
  refresh: PropTypes.number.isRequired,
  setRefresh: PropTypes.func.isRequired,
  showJobTypePill: PropTypes.bool,
  loomJob: PropTypes.bool,
  onEditJob: PropTypes.func,
  onRegenerateJob: PropTypes.func,
};

export default React.memo(JobDetails);
